import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Datasources } from '../../api/Datasources';
import { useToasts } from '../../contexts/toasts';
import { Badge, Breadcrumb, Button, Card, Col, Container, Form, Modal, Row, Table } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Alert, Tooltip } from 'antd';
import AppLoader from '../../components/common/AppLoader';
import { createQueryDetails, datasourceOptions, formatDate, getConnectionDetails, setObjectType } from '../../utils';
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useAuth } from "../../contexts/auth";
import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UserItem from "../../components/common/UserItem";
import { useFullScreenLoader } from "../../contexts/fullScreenLoader";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { DatabaseDatasource, Datasource, FileSystemDatasource, MessageBrokerDatasource, ObjectStorageDatasource } from "../../models/Datasource";
import { VizClient } from '../../api/VizClient';

const datasourceIcons = datasourceOptions.reduce((acc, curr) => {
  acc[curr.value] = curr.icon;
  return acc;
}, {} as Record<string, IconDefinition>);

export default function DatasourceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [datasource, setDatasource] = useState<Datasource>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const { loggedUser } = useAuth();

  const showIcons = useMemo(() => {
    return (datasource?.accessLevel === "TEAM" && datasource?.organization?.id === loggedUser?.organizationId)
      || (datasource?.accessLevel === "PRIVATE" && datasource?.owner?.id === loggedUser?.sub);
  }, [datasource, loggedUser]);

  useEffect(() => {
    initDatasource();
    // eslint-disable-next-line
  }, []);

  const initDatasource = () => {
    Datasources.getDatasource(+id!)
      .then(
        ({ data: ds }) => setDatasource(ds),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasources-error', message, `Data Source Error ${status ? `(${status})` : ''}`, 'danger');
          setError(true);
        }
      )
  }

  const onEditDatasource = () => navigate(`/datasources/${id}/edit`);
  const onDeleteDatasource = () => setShowDeleteModal(true);
  const handleDeleteClose = () => setShowDeleteModal(false);
  const handleCopyDatasource = () => {
    navigator.clipboard.writeText(JSON.stringify(datasource, null, 2));
    showToast("datasource-copy", "Datasource copied to clipboard");
  }

  const testDatasource = () => {
    showLoader();
    const connectionDetails = getConnectionDetails(datasource!);
    const body = createQueryDetails(connectionDetails);

    const promise = datasource!.type === 'messageBroker'
      ? VizClient.getTopics(body, datasource!.driverType)
      : VizClient.getDatabases(body, datasource!.driverType);

    promise.then(
      ({ data: res }) => {
        hideLoader();
        if (!res) showToast('connection-error', "Error while connecting to data source", "Connection Error", 'danger');
        else showToast('connection-success', "Successfully connected to data source", "Connection Valid", 'success');
      },
      err => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('connection-error', message, `Connection Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }

  const deleteDatasource = () => {
    showLoader();

    Datasources.deleteDatasource(datasource!.id as number)
      .then(
        () => {
          showToast('datasource-delete-success', 'Datasource deleted successfully.', 'Success', 'success');
          hideLoader();
          handleDeleteClose();
          navigate('/datasources');
        },
        () => {
          showToast('datasource-delete-error', 'Could not delete datasource.', 'Datasource Error', 'danger');
          hideLoader();
        }
      );
  };

  const renderMessageBrokerFields = () => {
    return setObjectType<MessageBrokerDatasource>(datasource) && (
      <>
        <tr>
          <th scope="col">Brokers</th>
          <td className="text-end">{datasource.brokers}</td>
        </tr>

        {datasource.username && (
          <tr>
            <th scope="col">Username</th>
            <td className="text-end">{datasource.username}</td>
          </tr>
        )}
        {datasource.password && (
          <tr>
            <th scope="col">Password</th>
            <td className="text-end">{datasource.password}</td>
          </tr>
        )}
        <tr>
          <th scope="col">CA File</th>
          <td className="text-end">{datasource.cafile || <small className="text-muted">-- empty --</small>}</td>
        </tr>
        <tr>
          <th scope="col">Cert File</th>
          <td className="text-end">{datasource.certfile || <small className="text-muted">-- empty --</small>}</td>
        </tr>
        <tr>
          <th scope="col">Key File</th>
          <td className="text-end">{datasource.keyfile || <small className="text-muted">-- empty --</small>}</td>
        </tr>
        <tr>
          <th scope="col">Key Server Config</th>
          <td className="text-end">{datasource.keyServerConfig || <small className="text-muted">-- empty --</small>}</td>
        </tr>
      </>
    )
  }

  const renderObjectStorageFields = () => {
    return setObjectType<ObjectStorageDatasource>(datasource) && (
      <>
        <tr>
          <th scope="col">Host</th>
          <td className="text-end">{datasource.host}</td>
        </tr>
        <tr>
          <th scope="col">Port</th>
          <td className="text-end">{datasource.port}</td>
        </tr>
        <tr>
          <th scope="col">Bucket</th>
          <td className="text-end">{datasource.bucket}</td>
        </tr>
        <tr>
          <th scope="col">Secure</th>
          <td className="text-end">
            <Form.Check type="checkbox" id="datasource-secure" checked={datasource.secure} disabled />
          </td>
        </tr>
        <tr>
          <th scope="col">Access Key</th>
          <td className="text-end">{datasource.accessKey}</td>
        </tr>
        <tr>
          <th scope="col">Secret Key</th>
          <td className="text-end">
            <b className="me-2">••••••</b>
            <Tooltip title={datasource.secretKey}>
              <FontAwesomeIcon icon={solid('eye')} size="sm" />
            </Tooltip>
          </td>
        </tr>
        <tr>
          <th scope="col">Prefix path</th>
          <td className="text-end">{datasource.prefixPath || <span className="text-muted">-- empty --</span>}</td>
        </tr>
        <tr>
          <th scope="col">Region Name</th>
          <td className="text-end">{datasource.regionName || <span className="text-muted">-- empty --</span>}</td>
        </tr>
      </>
    )
  }

  const renderDatabaseFields = () => {
    return setObjectType<DatabaseDatasource>(datasource) && (
      <>
        <tr>
          <th scope="col">Database</th>
          <td className="text-end">{datasource.database}</td>
        </tr>
        <tr>
          <th scope="col">Host</th>
          <td className="text-end">{datasource.host}</td>
        </tr>
        <tr>
          <th scope="col">Port</th>
          <td className="text-end">{datasource.port}</td>
        </tr>
        <tr>
          <th scope="col">User</th>
          <td className="text-end">{datasource.user}</td>
        </tr>
        <tr>
          <th scope="col">Password</th>
          <td className="text-end">
            <b className="me-2">••••••</b>
            <Tooltip title={datasource.password}>
              <FontAwesomeIcon icon={solid('eye')} size="sm" />
            </Tooltip>
          </td>
        </tr>
        {datasource.catalogue && (
          <tr>
            <th scope="col">Catalogue</th>
            <td className="text-end">{datasource.catalogue}</td>
          </tr>
        )}
        {datasource.thriftHost && (
          <tr>
            <th scope="col">Thrift Host</th>
            <td className="text-end">{datasource.thriftHost}</td>
          </tr>
        )}
        {datasource.thriftPort && (
          <tr>
            <th scope="col">Thrift Port</th>
            <td className="text-end">{datasource.thriftPort}</td>
          </tr>
        )}
      </>
    )
  }

  const renderFileSystemFields = () => {
    return setObjectType<FileSystemDatasource>(datasource) && (
      <>
        <tr>
          <th scope="col">File Path</th>
          <td className="text-end">{datasource.path}</td>
        </tr>
        <tr>
          <th scope="col">File Name</th>
          <td className="text-end">{datasource.filename}</td>
        </tr>
        <tr>
          <th scope="col">File Extension</th>
          <td className="text-end">{datasource.extension}</td>
        </tr>
      </>
    )
  }

  const renderAsyncContent = () => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="Could not retrieve data source detail"
      />
    )

    if (!datasource) return (
      <div className="d-flex py-5 justify-content-center">
        <AppLoader size={300} />
      </div>
    )

    const icon = datasourceIcons[datasource.type] || solid('faucet-drip');

    return (
      <>
        <Card className="item-detail-group main bg-datasource">
          <FontAwesomeIcon className="item-icon" icon={icon} />

          <Row className="gx-0 justify-content-end">
            <Badge className="w-auto shadow">
              {datasource.accessLevel}
            </Badge>
          </Row>

          <div className="p-2 py-4">
            <Row className="gx-0 align-items-center">
              <Col className="pe-3">
                <h3 className="mb-0">{datasource.name}</h3>
                <p className="text-trailing mb-3">{datasource.description}</p>
              </Col>

              <div className="w-auto">
                <Tooltip title="Test Connection">
                  <button className="circle-button p-4 ms-3" onClick={testDatasource}>
                    <FontAwesomeIcon icon={solid('signal')} />
                  </button>
                </Tooltip>
                <Tooltip title="Copy to Clipboard">
                  <button className="circle-button p-4 ms-3" onClick={handleCopyDatasource}>
                    <FontAwesomeIcon icon={solid('clipboard')} />
                  </button>
                </Tooltip>
                {showIcons && (
                  <>
                    <Tooltip title="Edit">
                      <button className="circle-button p-4 ms-3" onClick={onEditDatasource}>
                        <FontAwesomeIcon icon={solid('pen')} />
                      </button>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <button className="circle-button p-4 ms-3" onClick={onDeleteDatasource}>
                        <FontAwesomeIcon icon={solid('trash')} />
                      </button>
                    </Tooltip>
                  </>
                )}
              </div>
            </Row>
          </div>

          <Row className="gx-0 justify-content-between">
            <Col xs="auto">
              <div className="bg-highlight">
                <UserItem className="negative" user={datasource.owner!} organization={datasource.organization!} />
              </div>
            </Col>

            {/* <div className="d-flex w-auto align-items-center">
              {
                datasource.tags?.map((tag) => <Badge className="m-1" pill={true}>{tag}</Badge>)
              }
            </div> */}
          </Row>

          <div className="item-dates">
            <div className="d-inline-block">
              <b>Created:</b>
              <span className="ms-1">{formatDate(datasource.createDateTime!)}</span>
            </div>
            <div className="d-inline-block ms-2">
              <b>Last Update:</b>
              <span className="ms-1">{formatDate(datasource.updateDateTime!)}</span>
            </div>
          </div>
        </Card>

        <Card className="item-detail-group mb-3">
          <div className="item-detail-highlight">
            <FontAwesomeIcon icon={solid('faucet-drip')} />
            <h5 className="d-inline ms-2 mb-0">Connection Details</h5>
          </div>

          <Card.Body>
            <Table className="mb-0">
              <tbody>
                <tr>
                  <th scope="col">Driver</th>
                  <td className="text-end">{datasource.driverType}</td>
                </tr>

                {datasource.type === 'messageBroker' && renderMessageBrokerFields()}
                {datasource.type === 'objectStorage' && renderObjectStorageFields()}
                {datasource.type === 'database' && renderDatabaseFields()}
                {datasource.type === 'fileSystem' && renderFileSystemFields()}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </>
    );
  }

  return (
    <Container className="my-3">
      <Helmet>
        <title>Data Source {datasource?.name || ""} Detail</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/datasources' }}>Data Sources</Breadcrumb.Item>
        <Breadcrumb.Item active>{datasource?.name}</Breadcrumb.Item>
      </Breadcrumb>

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Data Source</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete data source <b>{datasource && datasource.name}</b>?</p>

          <Alert
            type="warning"
            showIcon
            message="Remember, this operation cannot be undone."
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteDatasource}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
